/**
 * Shared frontend TypeScript types.
 *
 * API response types are defined here and shared across hooks and services.
 * These mirror the server-side response DTOs (server/src/modules/.../dto/).
 */

/** Standardised API error shape (api-design.md §7) */
export interface ApiError {
  status: number;
  code: string;
  message: string;
  requestId?: string;
  details?: string[];
}

/** Offset-paginated response envelope (invitations, passes, etc.) */
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

/** Cursor-paginated response envelope (access logs) */
export interface CursorPaginatedResponse<T> {
  data: T[];
  meta: {
    nextCursor: string | null;
    hasMore: boolean;
  };
}

/** User statuses */
export type UserStatus = 'pending' | 'active' | 'suspended' | 'deleted';

/** Property-scoped user roles */
export type UserRole =
  | 'org_admin'
  | 'property_manager'
  | 'security_supervisor'
  | 'resident'
  | 'enterprise_tenant_admin'
  | 'employee'
  | 'guard';

// ─── /me ─────────────────────────────────────────────────────────────────────

export interface MeProfile {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  platformRole: 'super_admin' | 'none';
  propertyRoles: Array<{
    propertyId: string;
    unitId?: string;
    unitName?: string;
    role: UserRole;
    organisationId?: string;
    subdomain?: string | null;
  }>;
  status: UserStatus;
  emailVerified: boolean;
  mfaEnabled?: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeviceSession {
  id: string;
  deviceName: string | null;
  platform: string | null;
  location: string | null;
  lastActiveAt: string;
  isCurrent: boolean;
}

export type WalkinNotifyMode =
  | 'always'
  | 'scheduled_hours'
  | 'auto_approve_from_favourites'
  | 'dnd';

export interface NotificationPreferences {
  notifyVisitorCheckin: boolean;
  notifyVisitorCheckout: boolean;
  notifyDeliveryArrival: boolean;
  notifyAutoClose: boolean;
  notifyAnnouncements: boolean;
  notifyDomesticWorkerAbsence: boolean;
  walkinMode: WalkinNotifyMode;
  walkinHoursFrom: string | null;
  walkinHoursUntil: string | null;
  dndEnabled: boolean;
  dndFrom: string | null;
  dndUntil: string | null;
}

// ─── Invitations ─────────────────────────────────────────────────────────────

export type InvitationStatus =
  | 'active'
  | 'awaiting_visitor_registration'
  | 'confirmed'
  | 'declined'
  | 'used'
  | 'expired'
  | 'cancelled';

export type InvitationType =
  | 'single_visit'
  | 'recurring'
  | 'domestic_worker'
  | 'delivery'
  | 'taxi'
  | 'event'
  | 'walk_in'
  | 'employee'
  | 'trusted_operator';

export interface Invitation {
  id: string;
  visitorName: string;
  visitorPhone: string;
  visitorEmail: string | null;
  invitationType: InvitationType;
  status: InvitationStatus;
  validFrom: string;
  validUntil: string;
  gateId: string;
  gateName: string;
  partySize?: number;
  notes: string | null;
  vehiclePlate: string | null;
  vehicleMake: string | null;
  vehicleColour: string | null;
  qrTokenRef: string | null;
  pin: string | null;
  rsvpStatus: 'not_responded' | 'confirmed' | 'declined' | null;
  createdAt: string;
}

export interface InvitationPreview {
  visitorName: string;
  gateName: string;
  validFrom: string;
  validUntil: string;
  smsBody: string;
  emailSubject: string;
  emailBodyHtml: string;
}

export interface BulkInvitationVisitor {
  visitorName: string;
  visitorPhone: string;
  visitorEmail?: string;
  vehiclePlate?: string;
  idNumber?: string;
  partySize?: number;
}

export interface CreateBulkInvitationBody {
  eventName: string;
  eventDate: string;
  validFrom: string;
  validUntil: string;
  gateId?: string;
  visitors: BulkInvitationVisitor[];
}

export interface CreateBulkInvitationResult {
  createdCount: number;
  failed?: Array<{ visitorPhone: string; reason: string }>;
}

export type RegistrationLinkApprovalMode = 'open' | 'approval_required';

export interface CreateRegistrationLinkBody {
  title: string;
  description?: string;
  eventDate: string;
  eventLocation?: string;
  approvalMode: RegistrationLinkApprovalMode;
  capacity?: number;
  registrationDeadline?: string;
  validFrom?: string;
  validUntil?: string;
  gateId?: string;
}

export interface RegistrationLinkResult {
  id: string;
  slug: string;
  title: string;
  url: string;
  approvalMode: RegistrationLinkApprovalMode;
  capacity: number | null;
  status: string;
  createdAt: string;
}

export interface CreateInvitationBody {
  visitorName: string;
  visitorPhone: string;
  visitorEmail?: string;
  validFrom: string;
  validUntil: string;
  gateId?: string;
  note?: string;
  partySize?: number;
  vehiclePlate?: string;
  vehicleMake?: string;
  vehicleColour?: string;
  requirePreRegistration?: boolean;
  meetingLatitude?: number;
  meetingLongitude?: number;
  meetingLabel?: string;
}

// ─── Access Logs (Visitor History) ───────────────────────────────────────────

export type VisitStatus = 'granted' | 'checked_out' | 'expired' | 'denied';

export interface AccessLogEntry {
  id: string;
  visitorName: string;
  invitationType: InvitationType;
  status: VisitStatus;
  gateName: string;
  checkedInAt: string;
  checkedOutAt: string | null;
  durationMinutes: number | null;
  expectedPartySize?: number;
  actualPartySize?: number;
  guardName: string | null;
  notes: string | null;
  isFlagged?: boolean;
  flagReason?: string | null;
}

// ─── Recurring Passes ────────────────────────────────────────────────────────

export type RecurringPassStatus = 'active' | 'suspended' | 'expired' | 'revoked';

export type RecurrencePattern = 'daily' | 'weekdays' | 'specific_days' | 'custom';

export interface RecurringPass {
  id: string;
  visitorName: string;
  visitorPhone: string;
  recurrencePattern: RecurrencePattern;
  scheduleDays: number[] | null; // 0=Sun, 6=Sat
  entryTimeStart: string; // "08:00"
  entryTimeEnd: string; // "10:00"
  validFrom: string;
  validUntil: string | null;
  gateId: string;
  gateName: string;
  status: RecurringPassStatus;
  notes: string | null;
  passType: 'recurring' | 'domestic_worker' | 'contractor';
  createdAt: string;
}

export interface CreateRecurringPassBody {
  visitorName: string;
  visitorPhone: string;
  idType?: string;
  idNumber?: string;
  recurrencePattern: RecurrencePattern;
  scheduleDays?: number[];
  entryTimeStart: string;
  entryTimeEnd: string;
  validFrom: string;
  validUntil?: string;
  gateId?: string;
  notes?: string;
  passType: 'recurring' | 'domestic_worker' | 'contractor';
}

// ─── Delivery Passes ─────────────────────────────────────────────────────────

export type DeliveryPassStatus = 'active' | 'used' | 'expired' | 'cancelled';

export interface DeliveryPass {
  id: string;
  courierName: string | null;
  courierCompany: string | null;
  validFrom: string | null;
  validUntil: string;
  status: DeliveryPassStatus;
  usedAt: string | null;
  createdAt: string;
}

export interface CreateDeliveryPassBody {
  validUntil: string;
}

export interface DeliveryPassWithPin extends DeliveryPass {
  pin?: string;
}

// ─── Taxi Passes ─────────────────────────────────────────────────────────────

export interface TaxiPass {
  id: string;
  driverName: string | null;
  vehiclePlate: string | null;
  vehicleMake: string | null;
  ridePlatform: string | null;
  rideReference: string | null;
  validUntil: string;
  status: 'active' | 'used' | 'expired' | 'cancelled';
  usedAt: string | null;
  createdAt: string;
}

export interface CreateTaxiPassBody {
  vehiclePlate?: string;
  validityMinutes?: number;
}

export interface TaxiPassWithPin extends TaxiPass {
  pin?: string;
}

// ─── Walk-In Requests ────────────────────────────────────────────────────────

export type WalkInStatus = 'pending' | 'approved' | 'denied' | 'expired';

export interface WalkInRequest {
  id: string;
  visitorName: string;
  visitorPhone: string;
  vehiclePlate?: string | null;
  unitBeingVisited: string;
  guardName: string;
  status: WalkInStatus;
  createdAt: string;
  expiresAt: string;
}

// ─── Notifications ───────────────────────────────────────────────────────────

export type NotificationCategory =
  | 'visitor_check_in'
  | 'delivery_arrival'
  | 'walk_in_pending'
  | 'walk_in_resolved'
  | 'domestic_worker_no_show'
  | 'system';

export interface AppNotification {
  id: string;
  category: NotificationCategory;
  title: string;
  body: string;
  isRead: boolean;
  actionId: string | null; // e.g. walk-in requestId
  createdAt: string;
}

// ─── Favourites ──────────────────────────────────────────────────────────────

export interface Favourite {
  id: string;
  visitorName: string;
  visitorPhone: string;
  autoApprove: boolean;
  autoApproveTimeStart: string | null;
  autoApproveTimeEnd: string | null;
  createdAt: string;
}

// ─── Contacts ────────────────────────────────────────────────────────────────

export interface Contact {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  notes: string | null;
  createdAt: string;
}

// ─── DSAR ────────────────────────────────────────────────────────────────────

// NOTE: DsarType vocabulary aligned with server validation
// (server/src/modules/compliance/dto/compliance.dto.ts @IsIn).
export type DsarType = 'access' | 'erasure' | 'portability' | 'rectification' | 'restriction';

// Aligned with `enum dsar_status` in server/prisma/schema.prisma
// (submitted|processing|fulfilled|failed). Previous client values
// (pending|in_progress|rejected) never validated against the server.
export type DsarStatus = 'submitted' | 'processing' | 'fulfilled' | 'failed';

export interface DsarRequest {
  id: string;
  referenceNumber: string;
  requestType: DsarType;
  additionalDetails: string | null;
  status: DsarStatus;
  downloadUrl: string | null;
  submittedAt: string;
  fulfilledAt: string | null;
}

// ─── Gates ───────────────────────────────────────────────────────────────────

export interface Gate {
  id: string;
  name: string;
}

// ─── Public Visitor (no-auth) ─────────────────────────────────────────────────

export interface EstateBranding {
  estateName: string;
  /** Set when the invitation targets an enterprise unit. */
  companyName?: string | null;
  logoUrl: string | null;
  primaryColour: string | null;
}

export type PublicInvitationStatus =
  | 'active'
  | 'awaiting_visitor_registration'
  | 'confirmed'
  | 'declined'
  | 'used'
  | 'expired'
  | 'cancelled';

export interface PublicInvitation {
  token: string;
  status: PublicInvitationStatus;
  visitorName: string;
  hostName: string;
  unitReference: string | null;
  validFrom: string;
  validUntil: string;
  gateName: string;
  gateAddress: string | null;
  gateLatLng: { lat: number; lng: number } | null;
  meetingLatLng: { lat: number; lng: number } | null;
  meetingLabel: string | null;
  /** null when plaintext PIN was not retained (visitor received it via SMS at invitation time) */
  pin: string | null;
  notes: string | null;
  hasPhotoModule: boolean;
  requiresPreRegistration: boolean;
  requiresIdNumber: boolean;
  branding: EstateBranding;
}

export interface EventRegistrationDetails {
  linkToken: string;
  eventName: string;
  eventDate: string;
  /** null when the event has no specific registration window */
  validFrom: string | null;
  /** null when the event has no specific registration window */
  validUntil: string | null;
  estateName: string;
  logoUrl: string | null;
  hostMessage: string | null;
  capReached: boolean;
  cutoffPassed: boolean;
  requiresApproval: boolean;
}

export interface EventRegistrationResult {
  /** QR token present in open mode (no approval required); rendered client-side. */
  qrToken?: string;
  pin?: string;
  /** Present in approval mode */
  pending?: true;
}

// ─── Enterprise Tenant ────────────────────────────────────────────────────────

export interface EnterpriseTenant {
  id: string;
  unitReference: string | null;
  companyName: string | null;
  floor: string | null;
  receptionHours: string | null;
  createdAt: string;
  employeeCount?: number;
  contactAdmin?: { email: string; fullName: string } | null;
}

export interface EnterpriseEmployee {
  id: string;
  email: string;
  fullName: string;
  status: UserStatus;
  isActive: boolean;
  /** Null until the employee logs in for the first time. */
  lastLoginAt: string | null;
  createdAt: string;
}

export type WorkerPassStatus = 'active' | 'suspended' | 'revoked' | 'expired';

export interface WorkerPass {
  id: string;
  workerName: string;
  status: WorkerPassStatus;
  validFrom: string;
  validUntil: string | null;
  /** Present on the create response so the admin can copy / share. Absent on list. */
  qrToken?: string;
  createdAt?: string;
}

export interface CreateWorkerPassBody {
  workerName: string;
  workerPhone?: string;
  /** Optional. Server defaults to "now" — most worker passes start immediately. */
  validFrom?: string;
  validUntil?: string;
  /** Comma-separated work days (e.g. "MON,TUE,WED,THU,FRI"). Omit for daily. */
  workDays?: string;
  /** Access window start time in HH:mm format (e.g. "07:00"). Defaults to "08:00". */
  windowStartTime?: string;
  /** Access window end time in HH:mm format (e.g. "18:00"). Defaults to "18:00". */
  windowEndTime?: string;
}

// ─── Property Manager — Stats ─────────────────────────────────────────────────

export interface PropertyStats {
  eventsToday: number;
  eventsTodayDelta: number; // positive = up vs yesterday
  onPropertyNow: number;
  openIncidents: number;
  guardsOnline: number;
  guardsTotal: number;
}

// ─── Property Manager — Access Logs ──────────────────────────────────────────

export type PmVisitStatus = 'granted' | 'checked_out' | 'expired' | 'denied' | 'pending';

export interface PmAccessLogEntry {
  id: string;
  timestamp: string; // ISO 8601 check-in time
  status: PmVisitStatus;
  visitorName: string;
  hostUnit: string;
  hostName: string;
  gateName: string;
  gateId: string;
  invitationType: InvitationType;
  guardName: string | null;
  checkedOutAt: string | null;
  durationMinutes: number | null;
  expectedPartySize?: number;
  actualPartySize?: number;
  // detail fields (populated by EventDetailPanel GET)
  method: 'qr' | 'pin' | null;
  vehiclePlate: string | null;
  vehicleMake: string | null;
  vehicleColour: string | null;
  entryInstructions: string | null;
  invitationId: string | null;
  visitorPhotoUrl: string | null;
  timeline: Array<{ event: string; at: string }>;
  // Detain-on-exit flag state
  isFlagged?: boolean;
  flagReason?: string | null;
  flaggedAt?: string | null;
}

export interface PmAccessLogFilters {
  dateFrom?: string;
  dateTo?: string;
  gateIds?: string[];
  statuses?: PmVisitStatus[];
  invitationTypes?: InvitationType[];
  visitorName?: string;
  unit?: string;
  guardName?: string;
  cursor?: string;
  limit?: number;
  checkedInOnly?: boolean;
}

export interface AccessLogExportJob {
  jobId: string;
  status: 'queued' | 'processing' | 'done' | 'failed';
  downloadUrl: string | null;
}

// ─── Property Manager — Incidents ────────────────────────────────────────────

export type IncidentStatus = 'open' | 'under_review' | 'closed';

export interface IncidentSummary {
  id: string;
  referenceNumber: string;
  incidentType: string;
  title: string;
  status: IncidentStatus;
  assignedTo: string | null;
  createdAt: string;
  closedAt: string | null;
}

// ─── Property Manager — Walk-In Pending (PM view) ────────────────────────────

export interface PmWalkInRequest {
  id: string;
  visitorName: string;
  vehiclePlate?: string | null;
  residentUnit: string;
  arrivedAt: string;
  gateId: string;
  gateName: string;
}

// ─── Property Manager — Incidents (full detail) ────────────────────────────

export interface IncidentNote {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
}

export interface IncidentDetail {
  id: string;
  reference: string;
  type: string;
  category: string;
  description: string;
  gateName: string;
  gateId: string;
  guardName: string;
  guardId: string;
  loggedAt: string;
  status: IncidentStatus;
  assignedToName: string | null;
  assignedToId: string | null;
  resolutionNote: string | null;
  photoUrls: string[];
  notes: IncidentNote[];
  statusHistory: Array<{ status: IncidentStatus; changedAt: string; changedByName: string }>;
}

// ─── Property Manager — Users ─────────────────────────────────────────────────

export type PmUserStatus = 'active' | 'suspended' | 'pending';

export interface PmUser {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  role: UserRole;
  authMode: GuardAuthMode | null;
  unit: string | null;
  status: PmUserStatus;
  lastActiveAt: string | null;
}

export interface PendingRegistration {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  submittedAt: string;
  unit: string | null;
  /** Set only when the resident's typed unit matched a real unit at registration. */
  unitId: string | null;
  /** True when `unit` is a confirmed unit; false when it is the resident's free-text entry. */
  unitConfirmed: boolean;
}

export interface InviteResidentBody {
  fullName: string;
  email: string;
  phone: string;
  unitId: string;
}

export interface InviteGuardBody {
  fullName: string;
  email: string;
  phone: string;
  gateId: string;
}

export interface InviteSupervisorBody {
  fullName: string;
  email: string;
  phone: string;
}

// ─── Property Manager — Modules ───────────────────────────────────────────────

export interface PropertyModule {
  id: string;
  moduleKey: string; // e.g. 'GUARD_SCHEDULING', 'CORE'
  name: string;
  description: string;
  active: boolean;
  alwaysOn: boolean; // CORE and COMPLIANCE_DPA
  dependsOn: string[]; // module IDs this one requires
}

// ─── Property Manager — Guard Scheduling ────────────────────────────────────

export interface GuardDevice {
  id: string; // session UUID — pass to invalidate
  deviceId: string | null; // hardware/token identifier (s.device_id)
  deviceName: string | null;
  platform: string | null;
  appVersion: string | null;
  isOffline: boolean;
  lastSeenAt: string | null;
  lastSyncedAt: string | null;
  gateId: string | null; // assigned gate
  user: {
    id: string; // guard's user ID
    fullName: string;
    email: string;
  };
  createdAt: string;
}

export interface ShiftTemplate {
  id: string;
  name: string;
  startTime: string; // "HH:MM"
  endTime: string; // "HH:MM"
  daysOfWeek: number[]; // 0 = Sun … 6 = Sat
  createdAt: string;
}

export interface CreateShiftTemplateBody {
  name: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}

export interface UpdateShiftTemplateBody {
  name?: string;
  startTime?: string;
  endTime?: string;
  daysOfWeek?: number[];
}

export interface RosterEntry {
  id: string;
  templateId: string;
  templateName: string;
  startTime: string;
  endTime: string;
  guardUserId: string;
  guardName: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
}

export interface CreateRosterBody {
  templateId: string;
  guardUserId: string;
  effectiveFrom: string; // YYYY-MM-DD
  effectiveUntil?: string; // YYYY-MM-DD; omit for open-ended
}

export interface GuardActivityEvent {
  id: string;
  guardUserId: string;
  entryMethod: string; // 'qr' | 'pin' | 'manual'
  sourceType: string;
  visitorName: string;
  gateName: string | null;
  checkedInAt: string;
}

// Single-user detail shape returned by GET /users/:userId (richer than PmUser list shape)
export interface PmUserDetail {
  id: string;
  email: string;
  fullName: string;
  status: PmUserStatus;
  role: UserRole;
  unitId: string | null;
  gateId: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

// ─── Property Manager — Property Config ──────────────────────────────────────

export type PropertyType = 'residential' | 'commercial_single' | 'commercial_multi' | 'mixed';
export type GateDeviceMode = 'personal' | 'shared';

export interface PropertyConfig {
  id: string;
  name: string;
  address: string;
  propertyType: PropertyType;
  subdomain: string;
  registrationMode: 'manager_initiated' | 'self_registration';
}

export interface PropertyGate {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  entryInstructions: string | null;
  deviceMode: GateDeviceMode;
  zoneId: string | null;
}

export interface Unit {
  id: string;
  unitNumber: string;
  type: string;
  floor: string | null;
  zoneId: string | null;
  zoneName: string | null;
  isActive?: boolean;
  isSelfManaged?: boolean;
  destinationLatitude?: number | null;
  destinationLongitude?: number | null;
  residentCount?: number;
}

export interface BrandingConfig {
  propertyId: string;
  logoUrl: string | null;
  primaryColour: string | null;
  secondaryColour: string | null;
  fontFamily: string | null;
  emailSenderName: string | null;
  smsSenderId: string | null;
}

// ─── Property Manager — Announcements ────────────────────────────────────────

export type AnnouncementUrgency = 'normal' | 'info' | 'urgent' | 'emergency';

export interface UrgencyMeta {
  label: string;
  /** Vivid status colour for borders and selected-pill fills (fixed across modes). */
  color: string;
  /** Foreground colour for the label text — darkens on light paper for WCAG AA. */
  textColor: string;
}

export const URGENCY_META: Record<AnnouncementUrgency, UrgencyMeta> = {
  normal: { label: 'Normal', color: 'var(--text-tertiary)', textColor: 'var(--text-tertiary)' },
  info: { label: 'Info', color: 'var(--status-blue)', textColor: 'var(--status-blue-text)' },
  urgent: { label: 'Urgent', color: 'var(--status-amber)', textColor: 'var(--status-amber-text)' },
  emergency: {
    label: 'Emergency',
    color: 'var(--status-red)',
    textColor: 'var(--status-red-text)',
  },
};

export interface Announcement {
  id: string;
  title: string;
  body: string;
  urgency: AnnouncementUrgency;
  zoneId: string | null;
  imageUrl: string | null;
  publishedAt: string;
  archivedAt: string | null;
}

export interface CreateAnnouncementBody {
  title: string;
  body: string;
  urgency: AnnouncementUrgency;
  zoneId?: string;
}

// ─── Property Manager — Compliance / DPA ─────────────────────────────────────

export type PmDsarTransition = 'processing' | 'fulfilled' | 'failed';

export interface DsarQueueItem {
  id: string;
  referenceNumber: string;
  requestType: DsarType;
  status: string;
  submittedAt: string;
  processingStartedAt: string | null;
  fulfilledAt: string | null;
  additionalDetails: string | null;
  failureReason: string | null;
  requestedBy: {
    name: string | null;
    email: string | null;
  };
}

// ─── Property Manager — Analytics ────────────────────────────────────────────

export interface AnalyticsSummary {
  totalVisits: number;
  peakHour: string;
  busiestGate: string;
  busiestDayOfWeek: string;
  walkInRate: number;
}

export interface GateTrafficPoint {
  period: string;
  [gateName: string]: number | string;
}

export interface VisitorTypeBreakdown {
  type: string;
  count: number;
}

export interface HeatmapCell {
  day: number;
  hour: number;
  count: number;
}

// ─── Property Manager — Emergency Lockdown ───────────────────────────────────

export type EmergencyType = 'fire' | 'security_threat' | 'medical' | 'evacuation' | 'other';

/** Flat shape returned by GET /api/v1/properties/:propertyId/emergency/status */
export interface LockdownStatus {
  active: boolean;
  type: string | null;
  triggeredAt: string | null;
  triggeredBy: string | null;
}

/** Shape returned by GET /api/v1/properties/:propertyId/emergency/lockdown */
export interface LockdownRecord {
  id: string;
  lockdownType: string;
  status: string;
  reason: string | null;
  triggeredAt: string;
  liftedAt: string | null;
}

// ─── Organisation Admin ───────────────────────────────────────────────────────

export interface OrgDashboard {
  organisationId: string;
  organisationName: string | null;
  totalProperties: number;
  activeProperties: number;
  totalUsers: number;
  totalGuards: number;
  todaysVisits: number;
  openIncidents: number;
}

export type OrgPropertyType =
  | 'residential_apartment'
  | 'gated_community'
  | 'master_planned_city'
  | 'enterprise_building_single_tenant'
  | 'enterprise_building_multi_tenant'
  | 'mixed_use'
  | 'other';

export interface OrgProperty {
  id: string;
  name: string;
  address: string;
  propertyType: OrgPropertyType;
  subdomain: string;
  status: string;
  registrationMode: 'manager_initiated' | 'self_registration';
  createdAt: string;
}

export interface CreateOrgPropertyBody {
  name: string;
  address: string;
  propertyType: OrgPropertyType;
  subdomain: string;
}

export interface OrgAdmin {
  id: string;
  email: string;
  fullName: string;
  status: string;
  lastLoginAt: string | null;
}

export interface CreateOrgAdminBody {
  email: string;
  fullName: string;
  phone?: string;
}

/** Server returns this subset on creation (no lastLoginAt yet) */
export interface CreateOrgAdminResult {
  id: string;
  email: string;
  fullName: string;
  status: string;
}

// ─── Super Admin — Organisations ─────────────────────────────────────────────

export interface Organisation {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'suspended' | 'decommissioned';
  propertiesCount: number;
  usersCount: number;
  contactName: string;
  contactEmail: string;
  createdAt: string;
}

export interface CreateOrganisationBody {
  name: string;
  type: string;
  contactName: string;
  contactEmail: string;
}

// ─── Super Admin — Health / Monitoring ───────────────────────────────────────

export interface AdminError {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
}

export interface AdminHealth {
  status: string;
  timestamp: string;
  activeUsers: number;
  activeProperties: number;
  redis: string;
  database: string;
}

export interface BullMQQueue {
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
}

export interface AdminIncidentSummary {
  total: number;
  open: number;
  underReview: number;
  incidents: Array<{
    id: string;
    propertyId: string;
    propertyName: string;
    type: string;
    title: string;
    status: string;
    referenceNumber: string;
    createdAt: string;
  }>;
}

// ─── Super Admin — Audit Log ─────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  actorId: string;
  actorEmail: string;
  actorRole: string;
  action: string;
  targetEntity: string;
  targetId: string | null;
  propertyId: string | null;
  ipAddress: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  isImpersonation: boolean;
}

export interface AuditLogFilters {
  cursor?: string;
  limit?: number;
  action?: string;
  actorUserId?: string;
  propertyId?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ─── Super Admin — Feature Flags ─────────────────────────────────────────────

export interface FeatureFlag {
  moduleId: string;
  name: string;
  defaultEnabled: boolean;
  description: string;
}

export interface UpdateFeatureFlagsBody {
  flags: Record<string, boolean>;
}

// ─── Super Admin — Platform Config ───────────────────────────────────────────

export interface PlatformConfig {
  sms: {
    primaryProvider: string;
    fallbackProvider: string;
    africasTalking: { senderId: string; status: string };
    vonage: { fromNumber: string; status: string };
  };
  resend: { apiKeyStatus: string; fromName: string };
  googleMaps: { apiKeyStatus: string };
  dataRetention: { defaultRetentionDays: number };
  privacyPolicy: { version: string; updatedAt: string };
  termsOfService: { version: string; updatedAt: string };
}

export interface UpdatePlatformConfigBody {
  africasTalking?: { senderId?: string };
  resend?: { fromName?: string };
  dataRetention?: { defaultRetentionDays?: number };
}

// ─── Super Admin — Impersonation ─────────────────────────────────────────────

export interface ImpersonationSession {
  status: 'impersonating';
  targetUserId: string;
  targetEmail: string;
  targetName: string;
  expiresAt: string;
}

export interface StartImpersonationBody {
  targetUserId: string;
  reasonCode: string;
  justification: string;
}

export interface OrgUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  status: UserStatus;
  propertyId: string | null;
}

// ── Resident self-registration (public) ──────────────────────────────────────

export interface PropertyRegistrationInfo {
  propertyId: string;
  propertyName: string;
  registrationEnabled: boolean;
  branding: {
    primaryColour: string | null;
    logoUrl: string | null;
  } | null;
}

export interface ResidentSelfRegistrationBody {
  propertyId: string;
  fullName: string;
  email: string;
  phone: string;
  unitIdentifier: string;
  note?: string;
  // KDPA §32 + §33 — required at submission
  privacyPolicyAccepted: boolean;
  privacyPolicyVersion: string;
  termsAccepted: boolean;
  termsVersion: string;
  ageEighteenPlus: boolean;
  ageAttestationVersion: string;
}

// ─── Guard Web App ───────────────────────────────────────────────────────────

export interface CheckedInVisitor {
  visitId: string;
  visitorName: string;
  hostUnit: string;
  checkedInAt: string; // ISO 8601
  invitationType: InvitationType;
  vehiclePlate?: string;
  /** Detain-on-exit flag state. */
  isFlagged?: boolean;
  /** Internal flag reason (management-facing; guard surface only). */
  flagReason?: string | null;
  flaggedAt?: string | null;
}

/** Response from flag / clear-flag endpoints. */
export interface VisitFlag {
  visitId: string;
  isFlagged: boolean;
  flagReason: string | null;
  flaggedAt: string | null;
}

export interface UpcomingVisitor {
  invitationId: string;
  visitorName: string;
  hostUnit: string;
  hostName: string;
  invitationType: InvitationType;
  validFrom: string;
  validUntil: string;
  vehiclePlate?: string;
}

export interface CreateWalkInBody {
  visitorName: string;
  visitorPhone: string;
  idType?: 'national_id' | 'passport' | 'military_id';
  idNumber?: string;
  unitId: string;
  /** For enterprise units — routes the request to a specific employee instead of the tenant admin */
  hostEmployeeUserId?: string;
  /** Vehicle registration plate (optional — not all visitors arrive by car) */
  vehiclePlate?: string;
}

export interface PinValidationResult {
  result: 'allow' | 'deny';
  visitId?: string;
  visitor?: { name: string; photoUrl: string | null };
  host?: { name: string; unit: string };
  invitationType?: InvitationType;
  partySize?: number;
  /** Contractor crew pass: true when this is a contractor recurring pass. */
  isContractor?: boolean;
  /** Contractor crew: workers already recorded inside today (prior batches). */
  crewInsideNow?: number;
  validFrom?: string;
  validUntil?: string;
  vehicleDetails?: string | null;
  reason?: string;
  guardMessage?: string;
}

export interface ShiftInfo {
  shiftId: string;
  guardId: string;
  gateId: string;
  startedAt: string;
  gateName: string;
}

export interface IncidentResult {
  incidentId: string;
  referenceNumber: string;
  createdAt: string;
}

export interface GuardDailyLogParams {
  cursor?: string;
  limit?: number;
  gateId?: string;
  search?: string;
}

export interface StartShiftBody {
  gateId: string;
  deviceId: string;
  deviceName?: string;
  shiftEnd?: string;
}

export interface ChangeShiftBody {
  incomingGuardId: string;
}

export interface ConfirmEntryResult {
  visitId: string;
  checkedInAt: string;
}

export interface AccessEvent {
  id: string;
  status: 'GRANTED' | 'DENIED' | 'WALK_IN' | 'ALERT';
  visitorName: string;
  hostUnit: string;
  timestamp: string;
  invitationType: InvitationType;
}

// ─── Mode B — Shared Device Authentication ───────────────────────────────────

/** Guard auth mode — how the guard authenticates */
export type GuardAuthMode = 'credential' | 'roster_only';

/** A roster-only guard (Mode B — no login credentials) */
export interface RosterGuard {
  id: string;
  fullName: string;
  badgeNumber: string | null;
}

/** Device session info for PM dashboard */
export interface DeviceSessionInfo {
  id: string;
  gateId: string;
  gateName: string;
  deviceName: string | null;
  pairedAt: string;
  lastSeenAt: string | null;
  isActive: boolean;
  pairedBy?: string;
}

/** Response from pairing code generation */
export interface PairingCodeResponse {
  code: string;
  expiresAt: string;
  gateId: string;
  gateName: string;
}

/** Response from device activation */
export interface DeviceActivationResponse {
  gateId: string;
  gateName: string;
  propertyId: string;
  propertyName: string;
}

/** Response from shift selection (Mode B) */
export interface ShiftSelectResponse {
  shiftId: string;
  guardId: string;
  guardName: string;
  gateId: string;
  gateName: string;
  startedAt: string;
}

/** Roster response from GET /guard/roster */
export interface GuardRosterResponse {
  guards: RosterGuard[];
  gateName: string;
  propertyName: string;
}

/** Body for creating a roster guard */
export interface CreateRosterGuardBody {
  fullName: string;
  badgeNumber?: string;
  gateId: string;
}

/** Body for generating a pairing code */
export interface GeneratePairingCodeBody {
  gateId: string;
  deviceName?: string;
}
